/**
 * The one mermaid instance this SPA uses, loaded on first need.
 *
 * mermaid is imported dynamically: it is a large library and most sessions never look at a diagram,
 * so paying for it in the main bundle would slow every page to speed up a few. The import is cached
 * by the module system, so the cost lands once per session, and the editor preview, the gallery
 * thumbnails and the exports all share the same initialised instance.
 */
type Mermaid = typeof import('mermaid').default;

let mermaidReady: Promise<Mermaid> | null = null;

/**
 * One initialisation per session. `startOnLoad` stays off because nothing here is rendered from the
 * DOM, and `securityLevel: 'strict'` strips scripts and event handlers from the source before it
 * becomes markup.
 *
 * HTML labels are off, at the top level and for flowcharts. With them on, labels are HTML inside a
 * `foreignObject`, and drawing such an SVG onto a canvas taints it, so the PNG export would fail.
 * Plain SVG text also makes each label addressable in the drawing.
 */
export async function loadMermaid(): Promise<Mermaid> {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((module) => {
      module.default.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: 'default',
        htmlLabels: false,
        flowchart: { htmlLabels: false },
      });
      return module.default;
    });
  }
  return mermaidReady;
}
